import { motion } from "framer-motion";
import { useEffect, useState } from "react";

/**
 * A stylised world map showing sourcing corridors from global
 * manufacturing hubs (Shanghai, Yokohama, Rotterdam, Hamburg, Dubai)
 * into Africa (Accra as anchor, plus Lagos, Nairobi, Johannesburg,
 * Casablanca). All coordinates use percentage of the viewBox so the
 * map remains crisp at any size.
 *
 * Corridors animate stroke-dashoffset on view, drawing in slow
 * elegant arcs. Hubs pulse subtly on a schedule.
 */

// [x, y] as percent of 1000x500 viewBox
const HUBS = [
  { id: "accra",         name: "Accra",         xy: [485, 275], anchor: true },
  { id: "lagos",         name: "Lagos",         xy: [510, 285], anchor: true },
  { id: "casablanca",    name: "Casablanca",    xy: [455, 210], anchor: true },
  { id: "nairobi",       name: "Nairobi",       xy: [580, 315], anchor: true },
  { id: "johannesburg",  name: "Johannesburg",  xy: [560, 400], anchor: true },
  { id: "rotterdam",     name: "Rotterdam",     xy: [478, 152] },
  { id: "hamburg",       name: "Hamburg",       xy: [530, 138] },
  { id: "dubai",         name: "Dubai",         xy: [615, 245] },
  { id: "shanghai",      name: "Shanghai",      xy: [795, 235] },
  { id: "yokohama",      name: "Yokohama",      xy: [850, 230] },
];

// Corridors: each origin routes to the nearest African destination.
// This reflects Maklan's real supplier corridors — Africa is the destination,
// not just Accra.
const CORRIDORS = [
  ["shanghai",  "accra"],
  ["yokohama",  "accra"],
  ["hamburg",   "casablanca"],
  ["rotterdam", "casablanca"],
  ["dubai",     "nairobi"],
  ["shanghai",  "johannesburg"],
  ["dubai",     "lagos"],
];

// Very simplified continent silhouettes for atmosphere (dots pattern).
// We place many small dots roughly matching continent shapes. The pattern
// suggests a map without being cartographically accurate.
const CONTINENT_DOTS = [
  // North America
  ...gen(120, 100, 200, 120, 240, 22, "na"),
  // South America
  ...gen(240, 280, 320, 130, 400, 20, "sa"),
  // Europe
  ...gen(460, 130, 560, 90, 90, 16, "eu"),
  // Africa
  ...gen(440, 210, 620, 200, 220, 22, "af"),
  // Middle East + Asia
  ...gen(560, 180, 900, 190, 200, 22, "as"),
  // Oceania
  ...gen(780, 380, 900, 60, 80, 18, "oc"),
];

function gen(x, y, w, h, count, radius, key) {
  // pseudo-random deterministic dots
  const out = [];
  let seed = key.charCodeAt(0) + key.charCodeAt(1);
  const rnd = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let i = 0; i < count; i++) {
    const px = x + rnd() * w;
    const py = y + rnd() * h;
    out.push({ x: px, y: py, r: 0.9 + rnd() * 0.6 });
  }
  return out;
}

function pointFor(id) {
  return HUBS.find((h) => h.id === id).xy;
}

// Build a smooth quadratic arc from a → b, bending upward (negative Y = up)
function arcPath([x1, y1], [x2, y2]) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  // Arc height proportional to distance
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const lift = Math.min(120, dist * 0.28);
  return `M ${x1} ${y1} Q ${mx} ${my - lift}, ${x2} ${y2}`;
}

export default function WorldMap() {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    // Trigger the draw shortly after mount so it animates on scroll into view
    const t = setTimeout(() => setInView(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div data-testid="world-map" className="relative w-full aspect-[2/1] border border-white/10 bg-[#040713] overflow-hidden">
      {/* subtle radial atmosphere */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 55%, rgba(197,160,89,0.08) 0%, rgba(4,7,19,0) 55%)",
        }}
      />

      <svg viewBox="0 0 1000 500" className="relative w-full h-full">
        <style>{`
          @keyframes dashFlow {
            from { stroke-dashoffset: 0; }
            to   { stroke-dashoffset: -44; }
          }
        `}</style>
        {/* Continent dot fill */}
        <g fill="#C5A059" opacity="0.18">
          {CONTINENT_DOTS.map((d, i) => (
            <circle key={i} cx={d.x} cy={d.y} r={d.r} />
          ))}
        </g>

        {/* Corridors — draw slowly with a soft glow, then a moving dash on top */}
        <g fill="none" strokeLinecap="round">
          {CORRIDORS.map(([from, to], i) => {
            const d = arcPath(pointFor(from), pointFor(to));
            return (
              <g key={`${from}-${to}-${i}`}>
                {/* base under-glow */}
                <motion.path
                  d={d}
                  stroke="#C5A059"
                  strokeWidth="2.5"
                  opacity="0"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.18 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 2.6, delay: 0.4 + i * 0.35, ease: [0.19, 1, 0.22, 1] }}
                />
                {/* solid gold corridor */}
                <motion.path
                  d={d}
                  stroke="#C5A059"
                  strokeWidth="1"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.9 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 2.6, delay: 0.4 + i * 0.35, ease: [0.19, 1, 0.22, 1] }}
                />
                {/* animated dash flowing along corridor */}
                <motion.path
                  d={d}
                  stroke="#ffffff"
                  strokeWidth="0.8"
                  strokeDasharray="3 8"
                  opacity="0"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 0.6 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 1, delay: 2.4 + i * 0.35 }}
                  style={{
                    animation: `dashFlow 4s linear ${2.4 + i * 0.35}s infinite`,
                  }}
                />
              </g>
            );
          })}
        </g>

        {/* Hub markers */}
        <g>
          {HUBS.map((h) => (
            <g key={h.id}>
              {h.anchor && (
                <motion.circle
                  cx={h.xy[0]}
                  cy={h.xy[1]}
                  r="14"
                  fill="#C5A059"
                  opacity="0.15"
                  initial={{ scale: 0.6 }}
                  animate={inView ? { scale: [0.6, 1.4, 0.6] } : {}}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  style={{ transformOrigin: `${h.xy[0]}px ${h.xy[1]}px` }}
                />
              )}
              <circle
                cx={h.xy[0]}
                cy={h.xy[1]}
                r={h.anchor ? 4 : 2.5}
                fill={h.anchor ? "#C5A059" : "#fff"}
              />
              <text
                x={h.xy[0] + 10}
                y={h.xy[1] + 3}
                fill={h.anchor ? "#C5A059" : "rgba(255,255,255,0.7)"}
                fontFamily="'Manrope', sans-serif"
                fontSize={h.anchor ? "11" : "9"}
                fontWeight={h.anchor ? 600 : 400}
                style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}
              >
                {h.name}
              </text>
            </g>
          ))}
        </g>
      </svg>

      {/* Corner labels */}
      <div className="absolute top-4 left-4 overline">Sourcing Corridors</div>
      <div className="absolute bottom-4 right-4 text-[10px] tracking-[0.28em] uppercase text-white/40">
        World → Africa
      </div>
    </div>
  );
}
